import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from 'vue'
import logo from '@/assets/maropost-logo.svg'
import type { VAutocomplete } from 'vuetify/components'
import { Account, useAccountService } from '@/lib/services'
// @ts-ignore - lodash types not available
import { debounce } from 'lodash'
import { useRoute } from 'vue-router'
import background from '@/assets/images/background.jpeg'

const itemsPerPage = 10

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectAccountPage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute()

const style = {
  backgroundImage: `url('${background}')`
}

// Simple validation function
const required = (fieldName: string) => {
  return (value: Account | null) => {
    if (!value) {
      return `${fieldName} is required`
    }
    return true
  }
}

const autocompleteRef = ref<InstanceType<typeof VAutocomplete> | null>(null)
const model = ref<Account | null>(null)
const $account = useAccountService()
const isFormValid = ref(false)
const isLoading = ref(false)
const items = ref<Account[]>([])
const hasItems = ref(false)
const search = ref('')
const page = ref(1)
const selecting = ref(false)
const accountId = ref(0)

onMounted(() => {
  if (route.query.accountId) {
    accountId.value = Number(route.query.accountId)
  }
})

const onModelUpdate = () => {
  selecting.value = true

  // reset after next tick or short delay
  setTimeout(() => {
    selecting.value = false
  }, 600)
}

/**
 * Triggered when the intersection observer detects the end of the item list.
 * Loads more items if the observer indicates the component is intersecting.
 *
 * @param {boolean} isIntersecting - Indicates if the component is currently intersecting.
 */
const onIntersect = (isIntersecting: boolean) => {
  if (isIntersecting && !$account.isFetching.value && hasItems.value) {
    loadMoreItems()
  }
}

const isLoadMoreEnabled = computed(() => {
  return (
    hasItems.value &&
    !$account?.isFetching?.value 
  )
})

/**
 * Loads more items if available and not already loading.
 */
const loadMoreItems = async () => {
  if (
    !hasItems.value ||
    $account.isFetching.value 
  )
    return // Prevent multiple requests
  page.value++
  await fetchItems()
}

/**
 * Fetches items from the API using the provided service.
 * Constructs the request parameters based on current state.
 */
const fetchItems = async () => {
  const searchKeyword = items.value.find((item) => {
      return item['name'] === search.value
    })? ''
      : search.value

  const config = {
    abort: true,
    params: {
      per_page: itemsPerPage, // Set limit for items per page
      page: page.value,
      name_cont: searchKeyword,
      sort_by: 'name',
      sort_order: 'asc'
    }
  }

  try {
    isLoading.value = true
    await $account.findAll(config)
    const data = ($account.items.value || []) as Account[]
    items.value = page.value == 1 ? [...data] : [...items.value, ...data]
    if (search.value && autocompleteRef.value) {
      autocompleteRef.value.menu = true
    }
    hasItems.value = data.length >= itemsPerPage
  } catch (error: unknown) {
    console.error('Error fetching items:', error)
  } finally {
    isLoading.value = false
  }
}
function onFocusHandler(isFocused: boolean) {
  isFocused && items.value.length < 10 && !$account.isFetching.value && fetchItems()
}
/**
 * Triggered when the search input changes. Resets the page to 1 and fetches items.
 */
const onSearchHandler = () => {
  if (!selecting.value) {
    page.value = 1
    hasItems.value = false
    fetchItems()
  }
}

// Debounced version of the search handler
const debouncedSearchHandler = debounce(onSearchHandler, 500) // Adjust delay as needed


function onSubmitHandler() {
  if (!model.value) return
  
  const accountId = model.value.id
  window.location.href = `/accounts/${accountId}/dashboard`
}

const __returned__ = { route, style, required, autocompleteRef, model, $account, isFormValid, isLoading, items, hasItems, search, page, itemsPerPage, selecting, accountId, onModelUpdate, onIntersect, isLoadMoreEnabled, loadMoreItems, fetchItems, onFocusHandler, onSearchHandler, debouncedSearchHandler, onSubmitHandler, get logo() { return logo } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})